/**
 * Shared types, routes and permission keys for §16 own delivery & driver
 * operations (ADR-0029, `src/modules/delivery` on the backend).
 *
 * `Delivery`/`DriverShift`/`DeliveryZone`/`Driver` carry no `currency` field
 * of their own — unlike a `Check` or a `Payment` — so every money figure here
 * is rendered through `useCurrency(branchId)`, resolving the branch's own
 * currency rather than assuming the tenant default. See `Money.tsx`.
 */
import {
  DeliveryLocationCaptureMode,
  DeliveryReturnDisposition,
  DeliveryStatus,
  DriverShiftStatus,
  DriverStatus,
  DriverVehicleType,
  PaymentMethod,
  ProofOfDeliveryType,
} from '@/lib/shared-types';

export {
  DeliveryLocationCaptureMode,
  DeliveryReturnDisposition,
  DeliveryStatus,
  DriverShiftStatus,
  DriverStatus,
  DriverVehicleType,
  ProofOfDeliveryType,
};

export const DELIVERY_STATUSES: DeliveryStatus[] = Object.values(DeliveryStatus);
export const DRIVER_VEHICLE_TYPES: DriverVehicleType[] = Object.values(DriverVehicleType);
export const DELIVERY_RETURN_DISPOSITIONS: DeliveryReturnDisposition[] =
  Object.values(DeliveryReturnDisposition);
export const PROOF_TYPES: ProofOfDeliveryType[] = Object.values(ProofOfDeliveryType);
export const DELIVERY_LOCATION_CAPTURE_MODES: DeliveryLocationCaptureMode[] =
  Object.values(DeliveryLocationCaptureMode);

/**
 * A delivery in `PICKED_UP`/`EN_ROUTE`/`ASSIGNED` is still road-active — the
 * only states the backend ever populates `lastLat`/`lastLng` for (§2.9), and
 * the only states worth polling a position for at all.
 */
export const ACTIVE_DELIVERY_STATUSES: DeliveryStatus[] = ['ASSIGNED', 'PICKED_UP', 'EN_ROUTE'];

export interface Delivery {
  id: string;
  tenantId: string;
  branchId: string;
  orderId: string;
  zoneId: string | null;
  driverId: string | null;
  driverShiftId: string | null;
  status: DeliveryStatus;
  feeAmount: string;
  feeChargeId: string | null;
  assignedAt: string | null;
  pickedUpAt: string | null;
  enRouteAt: string | null;
  deliveredAt: string | null;
  failedAt: string | null;
  failureReason: string | null;
  proofType: ProofOfDeliveryType | null;
  proofRef: string | null;
  proofNote: string | null;
  proofRecordedAt: string | null;
  tenderMethod: PaymentMethod | null;
  collectedAmount: string | null;
  collectedAt: string | null;
  returnDisposition: DeliveryReturnDisposition | null;
  returnNote: string | null;
  refundPaymentId: string | null;
  /**
   * A bearer secret, not a display field (redteam finding, now 192 bits of
   * entropy) — never log it, never console it, never place it in a URL this
   * app's own router or audit trail will see. The one legitimate use here is
   * copying the full `/track/:token` link straight to the clipboard; see
   * `copyTrackingLink` below.
   */
  trackingToken: string;
  lastLat: number | null;
  lastLng: number | null;
  lastLocationAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Driver {
  id: string;
  tenantId: string;
  userId: string;
  branchId: string;
  vehicleType: DriverVehicleType;
  vehiclePlate: string | null;
  status: DriverStatus;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  user?: { id: string; firstName: string; lastName: string } | null;
}

export interface DriverShift {
  id: string;
  tenantId: string;
  branchId: string;
  driverId: string;
  status: DriverShiftStatus;
  openedAt: string;
  closedAt: string | null;
  openingCash: string;
  expectedCash: string | null;
  countedCash: string | null;
  varianceCash: string | null;
  reconciledAt: string | null;
  reconciledById: string | null;
  notes: string | null;
  createdAt: string;
  /** Only present on `GET /delivery/driver-shifts/:id` — server-derived, live while OPEN. */
  liveExpectedCash?: string;
}

export interface DeliveryZone {
  id: string;
  tenantId: string;
  branchId: string;
  name: string;
  active: boolean;
  priority: number;
  areas: string[];
  maxDistanceKm: string | null;
  baseFee: string;
  perKmFee: string | null;
  minOrderAmount: string | null;
  freeDeliveryAboveAmount: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceabilityResult {
  serviceable: boolean;
  zoneId: string | null;
  zoneName: string | null;
  distanceKm: number | null;
  feeAmount: string;
  meetsMinimumOrder: boolean;
}

export interface DeliveryLocationPolicy {
  id?: string;
  tenantId?: string;
  branchId: string | null;
  captureMode: DeliveryLocationCaptureMode;
  retentionHours: number;
}

export interface DeliveryAnalyticsSummary {
  from: string;
  to: string;
  totalDeliveries: number;
  byStatus: Record<string, number>;
  avgMinutesAssignedToDelivered: number | null;
  avgMinutesPickedUpToDelivered: number | null;
  failureRate: number | null;
  byDriver: Array<{ driverId: string; delivered: number; failed: number }>;
}

/** Every route this epic's dashboard coverage calls. `/delivery/track/:token` is deliberately absent — that is the unauthenticated Customer-Web page, not a Dashboard screen. */
export const DELIVERY_ROUTES = {
  deliveries: '/delivery/deliveries',
  delivery: (id: string) => `/delivery/deliveries/${id}`,
  assign: (id: string) => `/delivery/deliveries/${id}/assign`,
  unassign: (id: string) => `/delivery/deliveries/${id}/unassign`,
  pickUp: (id: string) => `/delivery/deliveries/${id}/pick-up`,
  enRoute: (id: string) => `/delivery/deliveries/${id}/en-route`,
  deliver: (id: string) => `/delivery/deliveries/${id}/deliver`,
  fail: (id: string) => `/delivery/deliveries/${id}/fail`,
  cancel: (id: string) => `/delivery/deliveries/${id}/cancel`,
  resolveReturn: (id: string) => `/delivery/deliveries/${id}/resolve-return`,

  analyticsSummary: '/delivery/analytics/summary',

  locationPolicy: '/delivery/location/policy',

  zones: '/delivery/zones',
  zone: (id: string) => `/delivery/zones/${id}`,
  zoneServiceability: '/delivery/zones/serviceability',

  driverShifts: '/delivery/driver-shifts',
  driverShift: (id: string) => `/delivery/driver-shifts/${id}`,
  driverShiftClose: (id: string) => `/delivery/driver-shifts/${id}/close`,
  driverShiftReconcile: (id: string) => `/delivery/driver-shifts/${id}/reconcile`,

  drivers: '/delivery/drivers',
  driver: (id: string) => `/delivery/drivers/${id}`,
} as const;

/** `resource.action` keys, mirroring `PERMISSIONS.deliveries` etc. in the backend, one to one. */
export const DELIVERY_PERMISSIONS = {
  deliveriesView: 'deliveries.view',
  deliveriesDispatch: 'deliveries.dispatch',
  deliveriesUpdateStatus: 'deliveries.update_status',
  deliveriesManageReturn: 'deliveries.manage_return',
  driversView: 'drivers.view',
  driversManage: 'drivers.manage',
  driverShiftsView: 'driver_shifts.view',
  driverShiftsOpen: 'driver_shifts.open',
  driverShiftsClose: 'driver_shifts.close',
  driverShiftsReconcile: 'driver_shifts.reconcile',
  deliveryZonesView: 'delivery_zones.view',
  deliveryZonesManage: 'delivery_zones.manage',
  deliveryLocationConfigure: 'delivery_location.configure',
} as const;

/** Any read key opens the route, matching the Fleet/Integrations/CentralKitchen I-07 shape. */
export const DELIVERY_READ_PERMISSIONS = [
  DELIVERY_PERMISSIONS.deliveriesView,
  DELIVERY_PERMISSIONS.driversView,
  DELIVERY_PERMISSIONS.driverShiftsView,
  DELIVERY_PERMISSIONS.deliveryZonesView,
  DELIVERY_PERMISSIONS.deliveryLocationConfigure,
];

/** A delivery in one of these has already reached a terminal outcome; no further transition applies. */
export const TERMINAL_DELIVERY_STATUSES: DeliveryStatus[] = ['DELIVERED', 'FAILED', 'CANCELLED'];

/**
 * The customer-facing tracking URL for a delivery. Built client-side from the
 * env-configured storefront origin plus the raw token — never echoed into this
 * app's own router (no `?token=`), never logged, and the only consumer is
 * `navigator.clipboard.writeText`. See the redteam note on `Delivery.trackingToken`.
 */
export function trackingLinkFor(token: string): string {
  const base =
    (import.meta.env.VITE_STOREFRONT_URL as string | undefined)?.replace(/\/+$/, '') ??
    'https://order.tarabeeza.app';
  return `${base}/track/${token}`;
}

/** Surface the API's own message (§3 envelope) rather than a generic failure line. */
export function apiMessage(error: unknown, fallback: string): string {
  const payload = (error as { response?: { data?: { message?: string | string[] } } })?.response
    ?.data?.message;
  if (Array.isArray(payload)) return payload.filter(Boolean).join(' · ');
  if (typeof payload === 'string' && payload.trim()) return payload;
  return fallback;
}
